import React, { useLayoutEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RepostIcon } from '../Brand/Icon';
import UserImage from '../UserImage';
import UserReactionScope from '../UserReactionScope';
import ReactionBody from './ReactionBody';
import { createName } from '../../utils/func';
import { reactionEntry } from '../../utils/reactionReaderState';
import url from '../../utils/url';
import './index.css';

// A real link supplies keyboard/new-tab navigation. The surrounding article
// delegates ordinary taps, without wrapping profile or rich-text links in it.
export default function ReactionQuote({ quote, collapsed = false, onExpand, onNavigate, original = false }) {
  const { t } = useTranslation('brand');
  const navigate = useNavigate();
  const pointer = useRef(null);
  const article = useRef(null);
  const restoreFocus = useRef(false);
  useLayoutEffect(() => {
    if (!collapsed && restoreFocus.current) {
      restoreFocus.current = false;
      article.current?.querySelector('.reaction-quote__identity a')?.focus({ preventScroll: true });
    }
  }, [collapsed]);
  if (!quote) return null;
  const unavailable = quote.unavailable || !quote.user;
  const name = unavailable ? '' : createName(quote.user).trim();
  const href = url(`/opinion/${quote.id}`);
  const open = (event) => {
    if (unavailable || event.defaultPrevented) return;
    if (collapsed) {
      restoreFocus.current = article.current?.contains(document.activeElement)
        && document.activeElement?.tagName === 'BUTTON';
      onExpand?.(quote.id); return;
    }
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
    event.preventDefault();
    onNavigate?.();
    navigate(href, { state: { reactionEntry: reactionEntry() } });
  };
  const onCardClick = (event) => {
    if (unavailable) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    if (event.target.closest('a,button,input,video,audio,[data-no-swipe]') || window.getSelection()?.toString()) return;
    const start = pointer.current;
    if (start && Math.hypot(event.clientX - start.x, event.clientY - start.y) > 8) return;
    open(event);
  };
  return <article ref={article} className={`reaction-quote${collapsed ? ' reaction-quote--collapsed' : ''}`}
    data-reaction-id={quote.id} data-expanded={!collapsed} data-unavailable={Boolean(unavailable)}
    onPointerDown={event => { pointer.current = { x: event.clientX, y: event.clientY }; }}
    onClick={onCardClick}>
    <span className="reaction-quote__marker" aria-hidden="true"><RepostIcon /></span>
    {unavailable ? <p className="reaction-quote__unavailable">{t('quoteUnavailable')}</p> : <>
      <div className="reaction-quote__author">
        <Link className="reaction-quote__avatar" to={url(`/user/${quote.user.user_name}`)}
          aria-label={name} onClick={onNavigate}><UserImage picture={quote.user.picture} /></Link>
        <div className="reaction-quote__identity">
          {collapsed ? <button type="button" onClick={open} aria-expanded="false"
            aria-label={t('quoteExpand', { name })}>
            <strong>{name}</strong><span>@{quote.user.user_name}</span>
          </button> : <Link to={href} onClick={open} aria-label={t('quoteOpen', { name })}>
            <strong>{name}</strong><span>@{quote.user.user_name}</span>
          </Link>}
        </div>
        <div className="reaction-quote__score"><UserReactionScope scope={quote.scope} interactive={false} /></div>
      </div>
      {!collapsed && <div className="reaction-quote__text"><ReactionBody
        html={original ? quote.text_msg : quote.text_msg_translation || quote.text_msg}
        media={quote.media} reactionId={quote.id} textStatus={quote.text_status} /></div>}
    </>}
  </article>;
}
